import {LotProperties} from 'notebook/integrations/chemistry/custom-evals/LotPropertiesCustomEval';
import {StoichiometryCustomEvalOptions} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {ValueError} from 'notebook/tables/excess/Util';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

/**
 * Custom eval for the molecule (Compound) column that auto-links from the lot column.
 *
 * When a lot is selected:
 * - If molecule column is empty → auto-populate with the lot's parent molecule
 * - If molecule column already has the same parent molecule → no action
 * - If molecule column has a different molecule → show error
 *
 * The lot's properties are resolved via the same STOICHIOMETRY_TABLE_GET_LOT_PROPERTIES
 * resolution query used by the lot column. The resolution cache is shared, so this
 * doesn't trigger an additional network request.
 */
export function lotAutoLinkMoleculeCustomEval(
  typedValue: TypedValue,
  _columnDefinition: ColumnDefinition | null,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: StoichiometryCustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn,
  _resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  // cellDependencies[0] is the lot column's evaluated value (a BIOENTITY typed value)
  const lotCellValue = cellDependencies[0];

  // If no lot is selected, pass through the current molecule value
  if (lotCellValue == null || lotCellValue.type !== BaseTypedValueType.BIOENTITY) {
    return {val: typedValue, error: null, locked: false};
  }

  const lotId = lotCellValue.bioentitySummary.id;

  // Resolve the lot's properties via the shared resolution cache
  const {resolvedValue, error: resolutionError} = resolveFromResolutionQuery(
    lotCellValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_LOT_PROPERTIES,
      lotId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );

  // If resolution hasn't completed or errored, pass through
  if (resolutionError != null || resolvedValue == null || resolvedValue.type !== BaseTypedValueType.JSON) {
    return {val: typedValue, error: null, locked: false};
  }

  // Read the lot properties from the resolved JSON typed value
  const lotProperties = resolvedValue.jsonValue as LotProperties;

  const {parentMoleculeId, parentMoleculeName} = lotProperties;

  // If the lot has no parent molecule, pass through
  if (parentMoleculeId == null) {
    return {val: typedValue, error: null, locked: false};
  }

  // If the molecule column is empty, auto-populate with the lot's parent molecule
  if (typedValue == null || typedValue.type === BaseTypedValueType.STRING) {
    const isEmpty = typedValue == null || typedValue.rawValue == null || typedValue.rawValue.length === 0;
    if (isEmpty) {
      return {
        val: {
          type: BaseTypedValueType.BIOENTITY,
          bioentitySummary: {id: parentMoleculeId, name: parentMoleculeName ?? ''},
        } as TypedValue,
        error: null,
        locked: false,
      };
    }
  }

  // If the molecule column already has a bioentity
  if (typedValue?.type === BaseTypedValueType.BIOENTITY) {
    const currentMoleculeId = typedValue.bioentitySummary.id;

    // Same molecule — no action needed
    if (currentMoleculeId === parentMoleculeId) {
      return {val: typedValue, error: null, locked: false};
    }

    // Different molecule — show error
    const lotMoleculeName = parentMoleculeName ?? parentMoleculeId;
    return {
      val: typedValue,
      error: new ValueError(
        `Selected lot belongs to a different molecule (${lotMoleculeName}). ` +
          `If that is the desired lot, change the molecule to ${lotMoleculeName}. ` +
          `If ${lotMoleculeName} is not the desired compound, clear the lot or change it to one that matches this molecule.`
      ),
      locked: false,
    };
  }

  // For any other state, pass through
  return {val: typedValue, error: null, locked: false};
}
